import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb,
  Button,
  Card,
  Divider,
  Switch,
  theme,
} from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  TagOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { CustomerDetailResponse } from '@/types/api/customer';
import CategoryTag from '@/components/common/CategoryTag';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { getOrganizationMenuItems } from '@/constants/navigation.constants';
import { TimestampPair } from '@/components/common/TimestampDisplay';

interface CustomerHeaderProps {
  customer: CustomerDetailResponse;
  canEdit: boolean;
  onEditClick: () => void;
  onToggleStatus: () => void;
  toggling: boolean;
}

const CustomerHeader = ({
  customer,
  canEdit,
  onEditClick,
  onToggleStatus,
  toggling,
}: CustomerHeaderProps) => {
  const { t } = useTranslation('customers');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const elevation = useElevation();

  const currentColorScheme = {
    headerBg: token.colorBgContainer,
    headerAccent: token.colorPrimary,
    cardShadow: token.boxShadow,
    borderColor: token.colorBorderSecondary,
    highlightBg: token.colorPrimaryBg,
  };

  const currentFontSizes = {
    title: token.fontSizeHeading2,
    subtitle: token.fontSize,
    body: token.fontSize,
    label: token.fontSizeSM,
    menuItem: token.fontSizeLG,
  };

  return (
    <Card
      className="tw-mb-6"
      style={{
        ...elevation.card,
        background: currentColorScheme.headerBg,
      }}
    >
      {/* Back button + Breadcrumb aligned horizontally */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/customers')}
          style={{
            fontSize: currentFontSizes.body,
            padding: 0,
            height: 'auto',
            lineHeight: 1,
          }}
        >
          {t('customer.buttons.back')}
        </Button>
        <Breadcrumb
          items={[
            {
              title: t('customer.breadcrumbs.masterData'),
            },
            {
              title: <a onClick={() => navigate('/auth/customers')}>{t('customer.breadcrumbs.organization')}</a>,
              menu: {
                items: getOrganizationMenuItems(navigate, tCommon)?.filter(
                  (item): item is NonNullable<typeof item> => item !== null
                ),
              },
            },
            {
              title: t('customer.breadcrumbs.customer'),
            },
            {
              title: customer.name,
            },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Header content */}
      <div className="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-mb-6">
        <div className="tw-flex-1">
          <div className="tw-mb-2">
            <h1
              style={{
                fontSize: currentFontSizes.title,
                fontWeight: token.fontWeightStrong,
                lineHeight: 1.2,
                color: currentColorScheme.headerAccent,
                margin: 0,
              }}
            >
              {customer.name}
              {customer.nickname && (
                <span
                  style={{
                    fontSize: token.fontSizeLG,
                    color: token.colorTextTertiary,
                    fontWeight: 'normal',
                    fontStyle: 'italic',
                    marginLeft: '12px',
                  }}
                >
                  ({customer.nickname})
                </span>
              )}
            </h1>
            {customer.alternativeName && (
              <p
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextTertiary,
                  margin: '8px 0 0 0',
                }}
              >
                {customer.alternativeName}
              </p>
            )}
          </div>

        </div>

        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditClick}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            fontSize: currentFontSizes.body,
          }}
        >
          {t('customer.actions.edit')}
        </Button>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      <div className="tw-flex tw-gap-8 tw-flex-wrap" style={{ paddingTop: '8px' }}>
        {/* Customer Code */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('customer.labels.customerCodeLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {customer.customerCode || '-'}
          </span>
        </div>

        {/* Customer ID */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('customer.labels.customerIdLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {customer.customerId || '-'}
          </span>
        </div>

        {/* Customer Type */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('customer.labels.typeLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {customer.individual ? t('customer.customerTypes.individual') : t('customer.customerTypes.company')}
          </span>
        </div>

        {/* Categories */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <TagOutlined
            style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
          />
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('customer.labels.categoriesLabel')}
          </span>
          <div className="tw-flex tw-gap-1 tw-flex-wrap">
            {customer.categories && customer.categories.length > 0
              ? customer.categories.map((cat, idx) => {
                  const catObj = typeof cat === 'object' && 'categoryId' in cat ? cat : null;
                  const catName = catObj ? catObj.category : String(cat);
                  const catColor =
                    catObj && 'color' in catObj ? catObj.color || token.colorPrimary : token.colorPrimary;
                  const catId = catObj ? catObj.categoryId : idx;
                  return (
                    <CategoryTag
                      key={catId}
                      name={catName}
                      color={catColor}
                    />
                  );
                })
              : '-'}
          </div>
        </div>

        {/* Timestamps */}
        <TimestampPair
          createdAt={customer.createdAt}
          updatedAt={customer.updatedAt}
          direction="horizontal"
        />

        {/* Status with Action Button */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <CheckCircleOutlined
            style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
          />
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('customer.labels.statusLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {customer.isActive ? t('customer.status.active') : t('customer.status.inactive')}
          </span>
          <Switch
            checked={customer.isActive}
            onChange={onToggleStatus}
            loading={toggling}
            style={{
              transform: 'scale(0.8)',
            }}
          />
        </div>
      </div>
    </Card>
  );
};

export default CustomerHeader;
